<template>
  <nuxt-link
    class="content"
    :class="{
      completed,
      'on-going': onGoing,
      'next-completed': nextCompleted,
      'next-not-completed': !nextCompleted,
      'previous-completed': previousCompleted,
      'previous-not-completed': !previousCompleted,
      first: index === 0,
      last: index === length - 1,
      focused: focused,
    }"
    :to="path"
    @click.capture="handleClickPath"
  >
    <span class="progress">
      <span class="graphic-border-effect" />
      <span class="graphic">
        <icon name="checkbox" size="0.9rem" class="graphic-check" />
      </span>

      <span class="graphic-border" />
    </span>
    <span class="label">
      <span class="text">{{ text }}</span>
      <Icon
        v-if="isTrialItem"
        class="icon-gift"
        size="2rem"
        name="fluent-emoji-flat:wrapped-gift"
      />
    </span>
    <span v-if="isCompleted" class="mark-out" :class="{ wrong: score < 0.5 }">
      {{ scoreOutOf20 }} / 20
    </span>
  </nuxt-link>
</template>
<script lang="ts" setup>
interface Props {
  index: number;
  focused: boolean;
  libraryBookId: string;
  id: string;
  nbItemsCompleted: number;
  length: number;
  path: string;
  text: string;
  score: number;
  previousCompleted: boolean;
  nextCompleted: boolean;
  completed: boolean;
  onGoing: boolean;
  availableByOrder?: boolean;
  isParentChildPage?: boolean;
  lockedContent: string;
}
const props = defineProps({
  index: null,
  focused: { type: Boolean, default: false },
  libraryBookId: null,
  id: null,
  nbItemsCompleted: null,
  length: null,
  path: null,
  text: null,
  score: null,
  previousCompleted: { type: Boolean },
  nextCompleted: { type: Boolean },
  completed: { type: Boolean },
  onGoing: { type: Boolean },
  availableByOrder: { type: Boolean, default: true },
  isParentChildPage: { type: Boolean },
  lockedContent: null
});

const profile = useProfileStore();

const { currentContent } = inject(INJECT_CATEGORY_PAGE);

const { progressions: allProgressions } = inject(INJECT_PROGRESSION_EXERCISES);

const progression = computed(() =>
  allProgressions.value.find((pr) => pr.contentId === props.id)
);

const progressionDetails = computed(() => {
  if (!progression.value) return null;
  return progression.value.data as ExerciseDetailsProgression;
});

const isCompleted = computed(() => progressionDetails.value?.status === 'done');

const scoreOutOf20 = computed(() => {
  if (!progressionDetails.value) return 0;
  const points = progressionDetails.value?.points;
  const maxPoints = progressionDetails.value?.maxPoints;
  return getScoreOn20(points / maxPoints);
});

const { handleClickPath, isTrialItem } = useCategoryContentClick(
  {
    lockedContent: props.lockedContent,
    id: props.id,
  },
  props.path,
  profile.isParent && currentContent.value.type === 'childrenContent'
);
</script>
<style lang="scss" scoped>
.content {
  display: flex;
  position: relative;
  align-items: center;
  height: auto;
  gap: var(--gap_n);
  padding: 0 var(--padding_horizontal);

  // Fix Safari issue if before and after are inside progress element
  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0%;
    left: calc(var(--padding_horizontal) + 1.1rem);
    width: 0.1rem;
    height: calc(50% - 1.6rem);
    margin-left: -0.1rem;
    background-color: var(--color_neutral_100);
  }
  &::after {
    top: calc(50% + 1.6rem);
  }

  &.previous-completed {
    &::before {
      background: var(--color_secondary);
    }
  }

  &.completed {
    &.previous-completed {
      &::before {
        background: var(--color_secondary);
      }
    }

    &.previous-not-completed {
      &::before {
        background: var(--color_neutral_100);
      }
    }
    &.next-completed {
      &::after {
        background: var(--color_secondary);
      }
    }
    &.next-not-completed {
      &::after {
        background: var(--color_secondary);
      }
    }
  }

  &.first {
    &::before {
      display: none;
    }
  }
  &.last {
    &::after {
      display: none;
    }
  }
}
.progress {
  display: flex;
  position: relative;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  .graphic-border {
    display: flex;
    position: absolute;
    z-index: 1;
    align-items: center;
    justify-content: center;
    width: 3.2rem;
    height: 3.2rem;
    border-width: var(--stroke_thickness_m);
    border-style: solid;
    border-radius: 50%;
    border-color: var(--color_secondary_lighter);
  }
  .graphic-border-effect {
    display: flex;
    position: absolute;
    z-index: 0;
    align-items: center;
    justify-content: center;
    width: 3.2rem;
    height: 3.2rem;
    border-width: var(--stroke_thickness_m);
    border-style: solid;
    border-radius: 50%;
    border-color: var(--color_secondary_lighter);
  }

  .graphic {
    display: flex;
    position: absolute;
    z-index: 1;
    align-items: center;
    justify-content: center;
    width: 2.2rem;
    height: 2.2rem;
    border-width: var(--stroke_thickness_n);
    border-style: solid;
    border-radius: 50%;
    border-color: var(--color_secondary_lighter);
    background: var(--color_neutral_80);
    &-check {
      opacity: 0;
      color: var(--color_neutral_25);
    }
  }
}
.focused {
  .progress {
    .graphic-border-effect {
      animation-name: animate-scale;
      animation-duration: 800ms;
      animation-timing-function: $ease-out-quad;
      animation-iteration-count: infinite;
      animation-direction: alternate;
    }
  }
}
.completed {
  .progress {
    .graphic {
      background: var(--color_secondary);
      &-check {
        opacity: 1;
      }
    }
  }
}
.on-going {
  .progress {
    .graphic {
      background: var(--color_secondary_light);
      &-check {
        opacity: 0;
      }
    }
  }
}

.label {
  position: relative;
  flex-grow: 3;
  padding: 1.4rem 0;
  transition: color 300ms $ease-out-quad;
  color: var(--color_neutral_900);
  text-align: left;
  @include font('s', '400', 'secondary');
  .icon-gift {
    margin-left: 0.6rem;
  }
  .text {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      z-index: 0;
      bottom: -0.5rem;
      left: 0;
      width: 100%;
      height: 0.5rem;
      transform: scaleX(0);
      transform-origin: 0%;
      transition: transform 300ms $ease-out-quad;
      background: var(--color_secondary);
      filter: blur(0);
    }
  }
}
a {
  &:hover,
  &:focus,
  &.active,
  &.disabled,
  &.router-link-active,
  &.router-link-exact-active {
    .label {
      .text {
        color: var(--color_secondary);
        &::before {
          transform: scale(1);
        }
      }
    }
  }
}

.mark-out {
  @include font('s', '600', 'secondary');

  flex-shrink: 0;
  color: var(--color_secondary);
  text-align: right;
  &.wrong {
    color: var(--color_error);
  }
}

@keyframes animate-scale {
  0% {
    transform: scale(1);
    opacity: 0;
  }
  40% {
    border-width: 0.3rem;
    border-color: var(--color_secondary);
    opacity: 1;
  }
  60% {
    transform: scale(0.8);
    border-width: 0.6rem;
  }
  100% {
    transform: scale(0.8);
    border-width: 0.6rem;
  }
}
</style>
